/**
 * 開発者ワンクリックログイン（dev 限定）— 多層防御の第 5 層（ARCHITECTURE §7.11）。
 *
 * 描画条件は **AND** で 2 つ:
 *   1. `import.meta.env.VITE_DEV_LOGIN === '1'`（ビルド時フラグ。本番ビルドでは
 *      この定数が `undefined` に畳まれ、下のコンポーネントごと tree-shaking で消える）
 *   2. `GET /api/v1/public/config` の `devLogin === true`（**サーバ真実**）
 *
 * 1 だけで描画すると、dev フラグ付きの成果物を誤って本番に置いた瞬間にボタンが出る。
 * 2 だけで判定すると、public/config が改竄されただけで出る。両方要求すれば
 * 「ビルドと接続先の両方が dev」でなければ描画されない。
 *
 * なお、押しても本番 API にはルート自体が登録されていない（層 1）ので 404 になる。
 * このコンポーネントは資格情報を一切持たない。
 */
import * as React from 'react';
import { useMutation } from '@tanstack/react-query';
import { FlaskConical, TriangleAlert } from 'lucide-react';
import { Alert, AlertDescription, Button } from '@mail-sv/ui';
import { devLogin } from '../lib/auth.js';
import { errorMessageOf } from './states.js';

export function DevLoginButton({
  serverDevLogin,
  onSuccess,
}: {
  /** `/api/v1/public/config` の `devLogin`。取得前は undefined。 */
  serverDevLogin: boolean | undefined;
  onSuccess: () => void;
}): React.ReactElement | null {
  // 層 5-a: ビルド時フラグ。どちらも立てずに作った本番ビルドではここで必ず null になる。
  // VITE_DEMO_LOGIN は公開デモ専用（サーバ側は DEMO_LOGIN_ENABLED で開く）。
  if (import.meta.env.VITE_DEV_LOGIN !== '1' && import.meta.env.VITE_DEMO_LOGIN !== '1') return null;
  // 層 5-b: サーバ真実。未取得（undefined）のときも描画しない＝フェイルクローズ。
  if (serverDevLogin !== true) return null;

  return <DevLoginPanel onSuccess={onSuccess} />;
}

function DevLoginPanel({ onSuccess }: { onSuccess: () => void }): React.ReactElement {
  const mut = useMutation({
    mutationFn: () => devLogin(),
    onSuccess,
  });

  return (
    <div className="space-y-2 rounded-lg border border-warning/40 bg-warning/10 p-3">
      <p className="flex items-center gap-2 text-[13px] font-medium text-warning">
        <TriangleAlert className="size-4 shrink-0" aria-hidden />
        開発モード — この表示は本番環境には出ません
      </p>
      {mut.isError ? (
        <Alert variant="destructive">
          <AlertDescription>{errorMessageOf(mut.error)}</AlertDescription>
        </Alert>
      ) : null}
      <Button
        type="button"
        variant="outline"
        className="w-full border-warning/50 text-warning hover:bg-warning/15"
        onClick={() => mut.mutate()}
        loading={mut.isPending}
      >
        <FlaskConical aria-hidden />
        {import.meta.env.VITE_DEMO_LOGIN === '1' ? 'デモとしてログイン' : '開発者としてログイン'}
      </Button>
      <p className="text-[11px] text-muted-foreground">
        seed 済みのデモアカウントでログインします。2 要素認証だけを飛ばし、パスワード検証は通常どおり行われます。
      </p>
    </div>
  );
}
